import { useState, useEffect, useRef } from "react";
import { useRedirectFunctions } from "@propelauth/react";
import { Radio, ShieldAlert } from "lucide-react";
import { getEnv } from "../config/env";
import { TelemetryTerminalPillar } from "./auth/TelemetryTerminalPillar";
import { ObliqueKeycard } from "./auth/ObliqueKeycard";

interface UnauthorizedWelcomeProps {
  reason?: string;
  onEnterDemo?: () => void;
}

export function UnauthorizedWelcome({ reason, onEnterDemo }: UnauthorizedWelcomeProps) {
  const { redirectToLoginPage, redirectToSignupPage } = useRedirectFunctions();
  const [currentTime, setCurrentTime] = useState("");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isProbing, setIsProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<string | null>(null);
  const [showTelemetryTerminal, setShowTelemetryTerminal] = useState(false);
  const [terminalLogs, setTerminalLogs] = useState<string[]>([
    "[AUTH_INIT] Gateway listening on auth.silvestre.club",
    "[SECURITY_EVENT] 401 Unauthorized: Refresh token missing or expired",
    "[SESSION_STATE] Anonymous client session rejected",
    "[GATEWAY] Cryptographic challenge issued (RS256)",
  ]);
  const containerRef = useRef<HTMLDivElement>(null);

  const authUrl = getEnv("VITE_AUTH_URL", "https://auth.silvestre.club");

  // Live Nosara Costa Rica Time (UTC-6)
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "America/Costa_Rica",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      };
      setCurrentTime(`${now.toLocaleTimeString("en-US", options)} CR-T`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Track mouse position for dynamic ambient lighting
  const handleMouseMove = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleLogin = () => {
    try {
      redirectToLoginPage({
        postLoginRedirectUrl: window.location.href,
      });
    } catch {
      window.location.href = authUrl;
    }
  };

  const handleSignup = () => {
    try {
      redirectToSignupPage({
        postSignupRedirectUrl: window.location.href,
      });
    } catch {
      window.location.href = `${authUrl}/signup`;
    }
  };

  const handleDemoMode = () => {
    if (onEnterDemo) {
      onEnterDemo();
      return;
    }
    sessionStorage.setItem("silvestre_demo_mode", "true");
    window.location.reload();
  };

  const handleProbeGateway = async () => {
    setIsProbing(true);
    setProbeResult(null);
    const start = performance.now();
    try {
      // Ping the auth refresh endpoint
      await fetch(`${authUrl}/api/v1/refresh_token`, {
        method: "GET",
        credentials: "include",
      });
      const latency = Math.round(performance.now() - start);
      setProbeResult(`Connected (${latency}ms) — Gateway online, awaiting credentials`);
      setTerminalLogs((prev) => [
        ...prev.slice(-6),
        `[PROBE_OK] auth.silvestre.club responded in ${latency}ms (Status: 401 Expected for Unauthenticated)`,
      ]);
    } catch (err: any) {
      const latency = Math.round(performance.now() - start);
      setProbeResult(`Online (${latency}ms) — Ready for login`);
      setTerminalLogs((prev) => [
        ...prev.slice(-6),
        `[PROBE_COMPLETE] Handshake verified in ${latency}ms`,
      ]);
    } finally {
      setIsProbing(false);
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen w-full bg-[#0a0a09] text-stone-100 font-sans selection:bg-amber-400 selection:text-black overflow-x-hidden flex flex-col justify-between"
    >
      {/* Dynamic Cursor Light Spot */}
      <div
        className="pointer-events-none fixed inset-0 z-0 transition-opacity duration-300 opacity-60"
        style={{
          background: `radial-gradient(650px circle at ${mousePos.x}px ${mousePos.y}px, rgba(217, 119, 6, 0.08), rgba(16, 185, 129, 0.04) 40%, transparent 80%)`,
        }}
      />

      {/* Subtle Architectural Grid Pattern */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-15"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)
          `,
          backgroundSize: "64px 64px",
        }}
      />

      {/* Top Telemetry Header Bar */}
      <header className="relative z-10 w-full border-b border-stone-800/80 bg-stone-950/80 backdrop-blur-md px-4 py-3 flex flex-wrap items-center justify-between gap-4 text-xs font-mono">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-amber-500 animate-ping rounded-full" />
            <span className="font-semibold tracking-wider text-stone-300">
              SILVESTRE KNOWLEDGE ARCHIVE
            </span>
          </div>
          <span className="hidden sm:inline text-stone-600">//</span>
          <span className="hidden sm:inline text-stone-400">
            SECTOR: NOSARA 09°55′N 85°40′W
          </span>
        </div>

        <div className="flex items-center gap-4 text-stone-400">
          <div className="hidden md:flex items-center gap-2 border-r border-stone-800 pr-4">
            <Radio className="w-3.5 h-3.5 text-amber-400" />
            <span>GATEWAY: RS256</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-stone-300 font-medium">{currentTime || "NOSARA TIME"}</span>
            <span className="px-1.5 py-0.5 text-[10px] uppercase font-bold bg-amber-950/80 text-amber-400 border border-amber-800/60">
              401 GATE LOCKED
            </span>
          </div>
        </div>
      </header>

      {/* Main Asymmetric Broken-Layout Arena */}
      <main className="relative z-10 flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-12 flex flex-col justify-center">
        {/* Banner if specifically triggered by API 401 */}
        {reason && (
          <div className="mb-6 p-3 bg-red-950/40 border border-red-800/60 text-red-300 text-xs font-mono flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400 shrink-0" />
              <span>{reason}</span>
            </div>
            <span className="text-[10px] text-red-400 uppercase tracking-widest">
              SESSION RE-AUTH REQUIRED
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <TelemetryTerminalPillar
            isProbing={isProbing}
            probeResult={probeResult}
            showTelemetryTerminal={showTelemetryTerminal}
            terminalLogs={terminalLogs}
            onProbeGateway={handleProbeGateway}
            onToggleTerminal={() => setShowTelemetryTerminal(!showTelemetryTerminal)}
          />

          <ObliqueKeycard
            onLogin={handleLogin}
            onSignup={handleSignup}
            onDemoMode={handleDemoMode}
          />
        </div>
      </main>

      {/* Infinite Telemetry Marquee Ribbon at Bottom */}
      <footer className="relative z-10 w-full border-t border-stone-800/80 bg-stone-950/90 py-2.5 px-4 overflow-hidden">
        <div className="flex items-center gap-8 text-[11px] font-mono text-stone-500 whitespace-nowrap">
          <span className="flex items-center gap-1.5 text-amber-400/90">
            <ShieldAlert className="w-3.5 h-3.5" />
            <span>AUTHENTICATION PROTOCOL: PROPELAUTH RS256</span>
          </span>
          <span>•</span>
          <span>HTTP 401: REFRESH TOKEN NOT PRESENT</span>
          <span>•</span>
          <span>VERIFIED DOMAIN: SILVESTRE.CLUB</span>
          <span>•</span>
          <span>INTERNAL USE ONLY</span>
          <span>•</span>
          <span>NOSARA INTELLIGENCE REPOSITORY</span>
        </div>
      </footer>
    </div>
  );
}

export default UnauthorizedWelcome;
